/**
 * Canonical Circle upcoming playdate loader + selectors (Phase 3.2I).
 *
 * Home and Play must share this path so agreed Crissy Field (and peers)
 * cannot diverge between tabs. Circle-on never reads PlayRequest here.
 */

import type { CirclePlaydateDto } from '@/lib/play/circlePlaydateDto'
import { parseCirclePlaydatesListResponse } from '@/lib/play/parseCirclePlaydatesListResponse'

export function buildCircleUpcomingPlaydatesUrl(dogId: string): string {
  return `/api/play/playdates?dogId=${encodeURIComponent(dogId)}&scope=upcoming`
}

export function shouldLoadCircleUpcoming(flags: {
  circleEnabled: boolean
  playdatesEnabled: boolean
  authenticated: boolean
  dogId: string | null | undefined
}): boolean {
  return (
    flags.circleEnabled === true &&
    flags.playdatesEnabled === true &&
    flags.authenticated === true &&
    typeof flags.dogId === 'string' &&
    flags.dogId.trim().length > 0 &&
    flags.dogId !== 'ALL_DOGS'
  )
}

export type CircleUpcomingLoadResult = {
  ok: boolean
  status: number
  items: CirclePlaydateDto[]
  parsedOk: boolean
}

/** Fetch + parse upcoming CirclePlaydates for one selected dog. */
export async function loadCircleUpcomingPlaydates(
  dogId: string,
  init?: RequestInit
): Promise<CircleUpcomingLoadResult> {
  const res = await fetch(buildCircleUpcomingPlaydatesUrl(dogId), {
    credentials: 'include',
    cache: 'no-store',
    ...init,
  })
  const raw = res.ok ? await res.json().catch(() => null) : null
  const parsed = parseCirclePlaydatesListResponse(raw)
  return {
    ok: res.ok,
    status: res.status,
    items: parsed?.items ?? [],
    parsedOk: Boolean(parsed),
  }
}

/** Open statuses used by Play relationship/upcoming surfaces. */
export function selectOpenCirclePlaydates(
  items: readonly CirclePlaydateDto[]
): CirclePlaydateDto[] {
  return items.filter(
    (item) => item.status === 'proposed' || item.status === 'agreed'
  )
}

/** Agreed-only — Home + Play "Upcoming playdate" cards. */
export function selectAgreedCirclePlaydates(
  items: readonly CirclePlaydateDto[]
): CirclePlaydateDto[] {
  return [...items]
    .filter((item) => item.status === 'agreed')
    .sort(
      (a, b) =>
        new Date(a.scheduledStartAt).getTime() -
        new Date(b.scheduledStartAt).getTime()
    )
}

export function selectEarliestAgreedCirclePlaydate(
  items: readonly CirclePlaydateDto[]
): CirclePlaydateDto | null {
  return selectAgreedCirclePlaydates(items)[0] ?? null
}

/**
 * Earliest agreed playdate that has not started yet (true "Upcoming").
 * Falls back to null when only overdue agreed rows exist.
 */
export function selectEarliestFutureAgreedCirclePlaydate(
  items: readonly CirclePlaydateDto[],
  now: Date = new Date()
): CirclePlaydateDto | null {
  const nowMs = now.getTime()
  return (
    selectAgreedCirclePlaydates(items).find(
      (item) => new Date(item.scheduledStartAt).getTime() > nowMs
    ) ?? null
  )
}

/**
 * Resolve the open playdate the relationship sheet / We Met should target.
 *
 * When `focusedPlaydateId` is set, only that playdate wins (if it is still
 * open for the relationship). Never substitute the relationship's preferred
 * or upcoming playdate for a specifically selected past playdate.
 */
export function resolveSelectedOpenCirclePlaydate(input: {
  items: readonly CirclePlaydateDto[]
  relationshipId: string
  focusedPlaydateId?: string | null
  now?: Date
}): CirclePlaydateDto | null {
  const now = input.now ?? new Date()
  const forRelationship = selectOpenCirclePlaydates(input.items).filter(
    (item) => item.relationshipId === input.relationshipId
  )
  const focusedId = input.focusedPlaydateId?.trim() || null
  if (focusedId) {
    return forRelationship.find((item) => item.id === focusedId) ?? null
  }
  return selectPreferredOpenCirclePlaydate(forRelationship, now)
}

/**
 * Relationship UI still exposes one "selected" open playdate, but multiple
 * open rows can exist for the same relationship. Never last-write-wins.
 *
 * Priority (lower bucket wins):
 *   0) agreed + scheduledStartAt <= now  → oldest overdue first
 *   1) agreed + scheduledStartAt > now   → earliest upcoming
 *   2) proposed                         → earliest scheduled start
 *
 * Ties within a bucket: earlier scheduledStartAt, then stable id ascending.
 */
export function compareOpenCirclePlaydatePriority(
  a: CirclePlaydateDto,
  b: CirclePlaydateDto,
  now: Date = new Date()
): number {
  const nowMs = now.getTime()
  const rank = (p: CirclePlaydateDto): [number, number, string] => {
    const startMs = new Date(p.scheduledStartAt).getTime()
    const agreed = p.status === 'agreed'
    const bucket = agreed ? (startMs <= nowMs ? 0 : 1) : 2
    return [bucket, startMs, p.id]
  }
  const ra = rank(a)
  const rb = rank(b)
  if (ra[0] !== rb[0]) return ra[0] - rb[0]
  if (ra[1] !== rb[1]) return ra[1] - rb[1]
  return ra[2] < rb[2] ? -1 : ra[2] > rb[2] ? 1 : 0
}

/**
 * Pick the single open playdate the relationship UI should surface.
 * Prefer `compareOpenCirclePlaydatePriority` over map overwrite.
 */
export function selectPreferredOpenCirclePlaydate(
  candidates: readonly CirclePlaydateDto[],
  now: Date = new Date()
): CirclePlaydateDto | null {
  let preferred: CirclePlaydateDto | null = null
  for (const item of candidates) {
    if (item.status !== 'proposed' && item.status !== 'agreed') continue
    if (
      !preferred ||
      compareOpenCirclePlaydatePriority(item, preferred, now) < 0
    ) {
      preferred = item
    }
  }
  return preferred
}

export function indexOpenCirclePlaydatesByRelationship(
  items: readonly CirclePlaydateDto[],
  now: Date = new Date()
): Record<string, CirclePlaydateDto> {
  const next: Record<string, CirclePlaydateDto> = {}
  for (const item of selectOpenCirclePlaydates(items)) {
    const existing = next[item.relationshipId]
    if (
      !existing ||
      compareOpenCirclePlaydatePriority(item, existing, now) < 0
    ) {
      next[item.relationshipId] = item
    }
  }
  return next
}

/**
 * Merge a locally retained playdate after accept when the list response
 * briefly lags (Play Integrated retain behavior).
 *
 * Uses the same priority rules as indexOpenCirclePlaydatesByRelationship —
 * retained wins only when it outranks the current selection for that
 * relationship (never blind overwrite).
 *
 * Same-id forward progress is required: an agreed retain MUST replace a
 * stale proposed row with the same playdate id (accept race).
 */
export function mergeRetainedCirclePlaydate(
  byRelationship: Record<string, CirclePlaydateDto>,
  retain: CirclePlaydateDto | null | undefined,
  activeDogId: string,
  now: Date = new Date()
): Record<string, CirclePlaydateDto> {
  if (!retain) return byRelationship
  if (retain.status !== 'proposed' && retain.status !== 'agreed') {
    return byRelationship
  }
  const dogMatches =
    retain.initiatorDog.id === activeDogId ||
    retain.inviteeDog.id === activeDogId
  if (!dogMatches) return byRelationship

  const existingSameId = Object.values(byRelationship).find(
    (item) => item.id === retain.id
  )
  if (existingSameId) {
    if (
      !isCirclePlaydateStatusForwardProgress(
        existingSameId.status,
        retain.status
      )
    ) {
      return byRelationship
    }
    // Forward same-id progress (proposed → agreed): replace in place.
    const next = { ...byRelationship }
    if (existingSameId.relationshipId !== retain.relationshipId) {
      delete next[existingSameId.relationshipId]
    }
    next[retain.relationshipId] = retain
    return next
  }

  const current = byRelationship[retain.relationshipId]
  if (current && compareOpenCirclePlaydatePriority(retain, current, now) >= 0) {
    // Retain is equal or lower priority — keep the existing selection.
    return byRelationship
  }
  return { ...byRelationship, [retain.relationshipId]: retain }
}

/**
 * Rank open/terminal statuses so client state never moves backward.
 * proposed < agreed < terminal (completed/cancelled/…).
 */
export function circlePlaydateStatusRank(status: string): number {
  if (status === 'proposed') return 0
  if (status === 'agreed') return 1
  return 2
}

export function isCirclePlaydateStatusForwardProgress(
  currentStatus: string,
  incomingStatus: string
): boolean {
  return (
    circlePlaydateStatusRank(incomingStatus) >
    circlePlaydateStatusRank(currentStatus)
  )
}

/**
 * Reconcile a network upcoming list with local lifecycle guards.
 * - Drop playdates locally marked completed (tombstones).
 * - Never replace local agreed with fetched proposed (same id).
 * - Prefer local agreed when fetch lags after accept.
 * - Preserve multiple open playdates per relationship (past overdue + future).
 */
export function reconcileUpcomingCirclePlaydates(input: {
  fetched: readonly CirclePlaydateDto[]
  previous: readonly CirclePlaydateDto[]
  completedPlaydateIds: ReadonlySet<string>
  retain?: CirclePlaydateDto | null
  activeDogId: string
  now?: Date
}): CirclePlaydateDto[] {
  const previousById = new Map(input.previous.map((p) => [p.id, p]))

  const filtered = input.fetched.filter((item) => {
    if (input.completedPlaydateIds.has(item.id)) return false
    if (item.status !== 'proposed' && item.status !== 'agreed') return false
    const prev = previousById.get(item.id)
    // Forbid agreed → proposed regression from a stale response.
    if (prev?.status === 'agreed' && item.status === 'proposed') return false
    return true
  })

  // Prefer previous agreed over missing/lagging fetch for same id.
  const byId = new Map(filtered.map((p) => [p.id, p]))
  for (const prev of input.previous) {
    if (input.completedPlaydateIds.has(prev.id)) continue
    if (prev.status !== 'agreed') continue
    const fetched = byId.get(prev.id)
    if (!fetched) {
      byId.set(prev.id, prev)
    } else if (fetched.status === 'proposed') {
      byId.set(prev.id, prev)
    }
  }

  const retain = input.retain
  if (
    retain &&
    (retain.status === 'proposed' || retain.status === 'agreed') &&
    (retain.initiatorDog.id === input.activeDogId ||
      retain.inviteeDog.id === input.activeDogId) &&
    !input.completedPlaydateIds.has(retain.id)
  ) {
    const existingSameId = byId.get(retain.id)
    if (!existingSameId) {
      byId.set(retain.id, retain)
    } else if (
      isCirclePlaydateStatusForwardProgress(
        existingSameId.status,
        retain.status
      )
    ) {
      byId.set(retain.id, retain)
    }
  }

  return Array.from(byId.values())
}

/**
 * Remove a completed playdate from an open list (optimistic complete).
 * Only the completed id is removed — sibling open playdates for the same
 * relationship (e.g. a future agreed) must remain.
 */
export function removeCompletedCirclePlaydateFromItems(
  items: readonly CirclePlaydateDto[],
  playdateId: string,
  _relationshipId?: string
): CirclePlaydateDto[] {
  return items.filter((item) => item.id !== playdateId)
}

/**
 * Upsert an agreed playdate into an open list (optimistic accept).
 * Replaces the same id only — does not drop sibling open playdates.
 */
export function upsertAgreedCirclePlaydateInItems(
  items: readonly CirclePlaydateDto[],
  agreed: CirclePlaydateDto
): CirclePlaydateDto[] {
  if (agreed.status !== 'agreed') return [...items]
  const without = items.filter((item) => item.id !== agreed.id)
  return [...without, agreed]
}
